// src/pages/school/Support.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Container, Row, Col, Card, Table, Button,
  Form, InputGroup, Modal, FloatingLabel, Badge,
  Spinner, Dropdown, Toast, ToastContainer, Nav
} from "react-bootstrap";
import {
  collection, onSnapshot, query, where, limit,
  updateDoc, doc, serverTimestamp, addDoc,
  getDoc, getDocs
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../services/firebase";
import useSchool from "../../hooks/useSchool";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
dayjs.extend(relativeTime);
dayjs.extend(duration);

/* ---------------- helpers ---------------- */
const toDate = (v) => (v?.toDate ? v.toDate() : v ? new Date(v) : null);
const fmt = (d, f = "DD MMM YYYY, HH:mm") =>
  d && dayjs(toDate(d)).isValid() ? dayjs(toDate(d)).format(f) : "—";

const StatusBadge = ({ status }) => {
  const s = String(status || "open");
  const map = { open: "warning", in_progress: "info", resolved: "success", closed: "secondary", on_hold: "dark" };
  return <Badge bg={map[s] || "secondary"} className="text-capitalize">{s.replace("_", " ")}</Badge>;
};

const SLA_HOURS = { low: 72, normal: 36, high: 12 };
const slaDue = (createdAt, priority = "normal") => {
  const base = toDate(createdAt);
  if (!base) return null;
  const hours = SLA_HOURS[String(priority || "normal").toLowerCase()] ?? 36;
  return dayjs(base).add(hours, "hour").toDate();
};

const SlaPill = ({ createdAt, priority, status }) => {
  const due = slaDue(createdAt, priority);
  if (!due) return null;
  const now = new Date();
  const ms = +due - +now;
  const over = ms < 0 && !["resolved", "closed"].includes(String(status));
  const d = dayjs(due);
  const text = d.isValid()
    ? (over ? `Overdue by ${dayjs.duration(-ms).humanize()}` : `Due ${d.fromNow()}`)
    : "—";
  return (
    <Badge bg={over ? "danger" : "light"} text={over ? "light" : "dark"} className="border">
      {text}
    </Badge>
  );
};

const initials = (name = "") =>
  name.trim().split(/\s+/).slice(0, 2).map(s => s[0]?.toUpperCase() || "").join("");

const PriorityPill = ({ level = "normal" }) => {
  const cls = `priority-pill priority-${level}`;
  return (
    <span className={cls}>
      <span className="dot" />
      {level}
    </span>
  );
};

/* ---------------- Ticket modal ---------------- */
function TicketModal({
  show, onHide, ticket, comments, posting,
  onAssignSelf, onUnassign,
  onSetStatus, onSetPriority,
  onAddPublicComment, onAddInternalNote,
  student, studentLoading
}) {
  const [note, setNote] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => { setNote(""); setFiles([]); setIsInternal(false); }, [ticket, show]);
  const onChoose = (e) => setFiles(Array.from(e.target.files || []));
  const removeAt = (i) => setFiles((f) => f.filter((_, idx) => idx !== i));

  return (
    <Modal show={show} onHide={onHide} centered size="lg" className="modal-elevated">
      <Modal.Header closeButton>
        <Modal.Title>Ticket – {ticket?.title || "—"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {!ticket ? null : (
          <Row className="g-3">
            <Col lg={8}>
              <div className="mb-2 small text-muted">#{ticket?.id}</div>
              <div className="mb-2">
                <b>Status:</b> <StatusBadge status={ticket?.status} />{" "}
                <SlaPill createdAt={ticket?.createdAt} priority={ticket?.priority} status={ticket?.status} />
              </div>
              <div className="mb-2"><b>Priority:</b> <PriorityPill level={ticket?.priority || "normal"} /></div>
              <div className="mb-2"><b>Category:</b> {ticket?.category || "general"}</div>
              <div className="mb-2"><b>Created by:</b> {ticket?.createdBy?.name || "—"}</div>
              <div className="mb-2"><b>Created:</b> {fmt(ticket?.createdAt)}</div>
              <div className="mb-2"><b>Updated:</b> {fmt(ticket?.updatedAt)}</div>

              {Array.isArray(ticket?.attachments) && ticket.attachments.length > 0 && (
                <div className="mb-2">
                  <b>Attachments:</b>
                  <div className="mt-2 d-flex flex-wrap">
                    {ticket.attachments.map((a, idx) => (
                      <a key={idx} href={a.url} target="_blank" rel="noreferrer" className="badge bg-light text-dark border me-2 mb-2">
                        <i className="bi bi-paperclip me-1" /> {a.name || "attachment"}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="mb-3">
                <b>Description</b>
                <div className="mt-1">{ticket?.description || "—"}</div>
              </div>

              <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
                <Dropdown onSelect={(k) => onSetStatus(k)}>
                  <Dropdown.Toggle size="sm" variant="outline-secondary"><i className="bi bi-flag" /> Status</Dropdown.Toggle>
                  <Dropdown.Menu>
                    {["open","in_progress","on_hold","resolved","closed"].map(s => (
                      <Dropdown.Item key={s} eventKey={s} active={ticket?.status===s} className="text-capitalize">
                        {s.replace("_"," ")}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                <Dropdown onSelect={(k) => onSetPriority(k)}>
                  <Dropdown.Toggle size="sm" variant="outline-secondary"><i className="bi bi-lightning" /> Priority</Dropdown.Toggle>
                  <Dropdown.Menu>
                    {["low","normal","high"].map(p => (
                      <Dropdown.Item key={p} eventKey={p} active={ticket?.priority===p} className="text-capitalize">
                        {p}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className="mb-2"><b>Add {isInternal ? "Internal Note" : "Public Comment"}</b></div>
              <FloatingLabel label="Write here…">
                <Form.Control
                  as="textarea" style={{ height: 110 }}
                  value={note} onChange={(e) => setNote(e.target.value)}
                  placeholder="Write a comment or note"
                />
              </FloatingLabel>

              <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
                <Form.Check
                  type="switch" id="internal-switch"
                  label="Mark as internal note"
                  checked={isInternal}
                  onChange={(e) => setIsInternal(e.target.checked)}
                />
              </div>

              <div className="mt-2">
                <Form.Label className="small">Attach files (optional)</Form.Label>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Form.Control ref={fileRef} type="file" multiple onChange={onChoose} />
                  {files.length > 0 && (
                    <div className="d-flex flex-wrap">
                      {files.map((f, i) => (
                        <span key={i} className="badge bg-light text-dark border me-2 mb-2">
                          <i className="bi bi-file-earmark me-1" />{f.name}
                          <Button size="sm" variant="link" className="text-danger ms-1 p-0" onClick={() => removeAt(i)}>
                            <i className="bi bi-x" />
                          </Button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <Card className="border-0 shadow-sm">
                <Card.Header className="bg-white fw-semibold">Assignee</Card.Header>
                <Card.Body>
                  {ticket?.assignee?.name ? (
                    <>
                      <div className="mb-2"><b>{ticket.assignee.name}</b></div>
                      <div className="small text-muted mb-3">{ticket.assignee.email || ticket.assignee.uid || ""}</div>
                      <div className="d-grid gap-2">
                        <Button size="sm" variant="outline-secondary" onClick={onUnassign}>Unassign</Button>
                      </div>
                    </>
                  ) : (
                    <div className="d-grid gap-2">
                      <Button size="sm" variant="outline-primary" onClick={onAssignSelf}>Assign to me</Button>
                    </div>
                  )}
                </Card.Body>
              </Card>

              <Card className="border-0 shadow-sm mt-3">
                <Card.Header className="bg-white fw-semibold">Comments</Card.Header>
                <Card.Body style={{ maxHeight: 320, overflow: "auto" }} className="comment-timeline">
                  {comments.length ? comments.map(c => (
                    <div key={c.id} className="mb-3">
                      <div className="small">
                        <b>{c.by?.name || "User"}</b>{" "}
                        <span className="text-muted">({c.by?.role || "user"})</span>
                        {c.type === "internal" && <Badge bg="dark" className="ms-2">Internal</Badge>}
                      </div>
                      <div className="small text-muted">{fmt(c.createdAt)}</div>
                      <div className="mt-1">{c.text}</div>
                      {Array.isArray(c.attachments) && c.attachments.length > 0 && (
                        <div className="mt-2 d-flex flex-wrap">
                          {c.attachments.map((a, i) => (
                            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="badge bg-light text-dark border me-2 mb-2">
                              <i className="bi bi-paperclip me-1" /> {a.name || "attachment"}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  )) : <div className="text-muted small">No comments yet</div>}
                </Card.Body>
              </Card>

              <div className="d-grid gap-2 mt-3">
                <Button
                  variant={isInternal ? "outline-dark" : "outline-success"}
                  onClick={() => {
                    if (!note.trim()) return;
                    const after = () => { setNote(""); setFiles([]); if (fileRef.current) fileRef.current.value = ""; };
                    if (isInternal) onAddInternalNote(note, files, after);
                    else onAddPublicComment(note, files, after);
                  }}
                  disabled={posting || !note.trim()}
                >
                  {posting ? "Posting…" : (isInternal ? "Add Internal Note" : "Add Public Comment")}
                </Button>
              </div>
            </Col>
          </Row>
        )}
      </Modal.Body>
    </Modal>
  );
}

/* ---------------- main page ---------------- */
export default function Support() {
  const { school, loading: loadingSchool, me } = useSchool();

  const meUid  = (me?.uid || me?.id || me?.auth?.uid || "").toString();
  const meName = me?.name || me?.displayName || "Staff";
  const roleRaw = String(me?.role || "").toLowerCase();
  const byRole = ["student","teacher","schooladmin","superadmin","admin"].includes(roleRaw) ? roleRaw : "teacher";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [qText, setQText] = useState("");
  const [status, setStatus] = useState("open");
  const [priority, setPriority] = useState("all");

  const [show, setShow] = useState(false);
  const [current, setCurrent] = useState(null);
  const [comments, setComments] = useState([]);
  const [posting, setPosting] = useState(false);

  const [toast, setToast] = useState({ show: false, bg: "dark", title: "", body: "" });
  const showToast = (body, { title = "Notice", bg = "dark", delay = 2200 } = {}) =>
    setToast({ show: true, bg, title, body, delay });

  const searchRef = useRef(null);

  // Live tickets feed
  useEffect(() => {
    if (!school?.id) return;
    setLoading(true);
    const colRef = collection(db, "schools", school.id, "tickets");
    const qy = query(colRef, where("schoolId", "==", school.id));
    const unsub = onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (+toDate(b.updatedAt) || 0) - (+toDate(a.updatedAt) || 0));
        setRows(list);
        setLoading(false);
      },
      (err) => {
        setLoading(false);
        Swal.fire({ icon: "error", title: "Could not load tickets", text: err?.message || "Try again" });
      }
    );
    return () => unsub();
  }, [school?.id]);

  // Comments stream for current ticket
  useEffect(() => {
    if (!school?.id || !current?.id) { setComments([]); return; }
    const colRef = collection(db, "schools", school.id, "tickets", current.id, "comments");
    const unsub = onSnapshot(colRef, (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (+toDate(a.createdAt) || 0) - (+toDate(b.createdAt) || 0));
      setComments(list);
    });
    return () => unsub();
  }, [school?.id, current?.id]);

  const filtered = useMemo(() => {
    const q = qText.trim().toLowerCase();
    return rows.filter(t => {
      if (status !== "all" && String(t.status || "open") !== status) return false;
      if (priority !== "all" && String(t.priority || "normal") !== priority) return false;
      if (!q) return true;
      const hay = [t.title, t.description, t.category, t.createdBy?.name, t.assignee?.name, t.createdBy?.branch].join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, qText, status, priority]);

  const counts = useMemo(() => {
    const acc = { all: rows.length };
    rows.forEach(r => { const s = String(r.status || "open"); acc[s] = (acc[s] || 0) + 1; });
    return acc;
  }, [rows]);

  const openTicket = (t) => { setCurrent(t); setShow(true); };

  // Actions
  const assignSelf = async () => {
    if (!current) return;
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        assignee: { uid: meUid, name: meName, email: me?.email || "" },
        updatedAt: serverTimestamp(),
      });
      showToast("Assigned to you.", { title: "Assigned", bg: "success" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not assign", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  const unassign = async () => {
    if (!current) return;
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        assignee: null,
        updatedAt: serverTimestamp(),
      });
      showToast("Unassigned.", { title: "Updated", bg: "success" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not unassign", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  const setStatusAction = async (newStatus) => {
    if (!current) return;
    setPosting(true);
    try {
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        status: newStatus,
        updatedAt: serverTimestamp(),
        ...(newStatus === "resolved" ? { resolvedAt: serverTimestamp() } : {})
      });
      showToast(`Status changed to ${newStatus}.`, { title: "Updated", bg: "success" });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not change status", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  // Upload helper for comment attachments (optional usage)
  const uploadMany = async (ticketId, filesArr) => {
    if (!filesArr?.length) return [];
    const out = [];
    for (const f of filesArr) {
      const safe = f.name.replace(/[^\w.\-]+/g, "_");
      const path = `schools/${school.id}/tickets/${ticketId}/comments/${Date.now()}_${safe}`;
      const r = ref(storage, path);
      await uploadBytes(r, f);
      const url = await getDownloadURL(r);
      out.push({ name: f.name, path, url, size: f.size || null, type: f.type || null });
    }
    return out;
  };

  const addPublicComment = async (text, files, after) => {
    if (!current) return;
    setPosting(true);
    try {
      const uploaded = await uploadMany(current.id, files);
      await addDoc(collection(db, "schools", school.id, "tickets", current.id, "comments"), {
        text: text.trim(),
        type: "public",
        by: { uid: meUid, name: meName, role: byRole },
        attachments: uploaded,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        lastCommentAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(current?.firstResponseAt ? {} : { firstResponseAt: serverTimestamp() }),
        status: current?.status === "open" ? "in_progress" : current?.status,
      });
      showToast("Comment added.", { title: "Success", bg: "success" });
      after?.();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not add comment", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  const addInternalNote = async (text, files, after) => {
    if (!current) return;
    setPosting(true);
    try {
      const uploaded = await uploadMany(current.id, files);
      await addDoc(collection(db, "schools", school.id, "tickets", current.id, "comments"), {
        text: text.trim(),
        type: "internal",
        by: { uid: meUid, name: meName, role: byRole },
        attachments: uploaded,
        createdAt: serverTimestamp(),
      });
      await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
        updatedAt: serverTimestamp(),
      });
      showToast("Internal note added.", { title: "Saved", bg: "success" });
      after?.();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Could not add note", text: err?.message || "Try again" });
    } finally { setPosting(false); }
  };

  if (loadingSchool) {
    return (
      <Card className="border-0 shadow-sm">
        <Card.Body className="py-5 text-center text-muted">
          <Spinner animation="border" size="sm" /> Loading…
        </Card.Body>
      </Card>
    );
  }
  if (!school?.id) return <div className="text-muted">No school selected.</div>;
// Update ticket priority
const setPriorityAction = async (level) => {
  if (!current) return;
  setPosting(true);
  try {
    await updateDoc(doc(db, "schools", school.id, "tickets", current.id), {
      priority: level,
      updatedAt: serverTimestamp(),
    });
    showToast(`Priority set to ${level}.`, { title: "Updated", bg: "success" });
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "Could not set priority",
      text: err?.message || "Try again",
    });
  } finally {
    setPosting(false);
  }
};

  return (
    <Container fluid className="p-0">
      {/* Header / Filters */}
      <Row className="mb-3 g-2 align-items-center support-toolbar">
        <Col>
          <h5 className="mb-0">Support – Admin</h5>
          <small className="text-muted">Manage and respond to tickets</small>
        </Col>
        <Col md={5}>
          <InputGroup>
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control
              ref={searchRef}
              placeholder="Search title, description, student, assignee, category…"
              value={qText}
              onChange={(e) => setQText(e.target.value)}
            />
            {qText && (
              <Button variant="outline-secondary" onClick={() => setQText("")}>
                Clear
              </Button>
            )}
          </InputGroup>
        </Col>
        <Col md="auto" className="text-end">
          <Nav
            variant="pills"
            activeKey={status}
            onSelect={(k) => setStatus(k || "all")}
            className="flex-wrap"
          >
            {["all","open","in_progress","on_hold","resolved","closed"].map(s => (
              <Nav.Item key={s} className="me-1 mb-1">
                <Nav.Link eventKey={s} className="text-capitalize">
                  {s.replace("_"," ")}
                  <span className="count badge bg-light text-dark ms-1">
                    {({ all: rows.length, ...rows.reduce((acc, r) => {
                      const st = String(r.status || "open");
                      acc[st] = (acc[st] || 0) + 1;
                      return acc;
                    }, {}) }[s]) || (s === "all" ? rows.length : 0)}
                  </span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </Col>
        <Col xs="auto">
          <Dropdown onSelect={(k) => setPriority(k || "all")}>
            <Dropdown.Toggle variant="outline-secondary" size="sm">
              <i className="bi bi-funnel" /> Priority: {priority}
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {["all","low","normal","high"].map(p => (
                <Dropdown.Item key={p} eventKey={p} active={priority === p} className="text-capitalize">
                  {p}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      {/* Tickets table */}
      <Card className="border-0 shadow-sm">
        <div className="table-responsive">
          <Table hover className="align-middle table-modern mb-0">
            <thead>
              <tr>
                <th className="sticky-th">Title</th>
                <th className="sticky-th">Student</th>
                <th className="sticky-th">Category</th>
                <th className="sticky-th">Priority</th>
                <th className="sticky-th">Status</th>
                <th className="d-none d-xl-table-cell sticky-th">Updated</th>
                <th className="d-none d-xxl-table-cell sticky-th">SLA</th>
                <th className="sticky-th">Assignee</th>
                <th className="text-end sticky-th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <Spinner animation="border" size="sm" /> Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">No tickets</td>
                </tr>
              ) : (
                filtered.map(t => (
                  <tr key={t.id}>
                    {/* Title */}
                    <td style={{ minWidth: 240 }}>
                      <div className="fw-semibold">{t.title || "—"}</div>
                      <div className="small text-muted">
                        {fmt(t.createdAt)}
                        {Array.isArray(t.attachments) && t.attachments.length > 0 && (
                          <span className="ms-2" title="Has attachments">
                            <i className="bi bi-paperclip" />
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Student */}
                    <td style={{ minWidth: 220 }}>
                      <div className="d-flex align-items-center gap-2">
                        {t.createdBy?.photoURL ? (
                          <img src={t.createdBy.photoURL} alt="" className="avatar-xs" />
                        ) : (
                          <span className="avatar-fallback">{initials(t.createdBy?.name || "U")}</span>
                        )}
                        <div>
                          <div className="fw-semibold">{t.createdBy?.name || "—"}</div>
                          <div className="small text-muted">{t.createdBy?.uid || ""}</div>
                          {t.createdBy?.branch && (
                            <span className="chip badge bg-light text-dark border mt-1">Branch: {t.createdBy.branch}</span>
                          )}
                        </div>
                      </div>
                    </td>

                    <td>{t.category || "general"}</td>
                    <td><PriorityPill level={String(t.priority || "normal")} /></td>
                    <td><StatusBadge status={t.status} /></td>
                    <td className="d-none d-xl-table-cell">{fmt(t.updatedAt)}</td>
                    <td className="d-none d-xxl-table-cell">
                      <SlaPill createdAt={t.createdAt} priority={t.priority} status={t.status} />
                    </td>

                    {/* Assignee */}
                    <td style={{ minWidth: 200 }}>
                      {t.assignee?.name ? (
                        <div className="d-flex align-items-center gap-2">
                          {t.assignee?.photoURL ? (
                            <img src={t.assignee.photoURL} alt="" className="avatar-xs" />
                          ) : (
                            <span className="avatar-fallback">{initials(t.assignee?.name || "S")}</span>
                          )}
                          <div>
                            <div className="fw-semibold">{t.assignee.name}</div>
                            <div className="small text-muted">{t.assignee.email || t.assignee.uid || ""}</div>
                          </div>
                        </div>
                      ) : <span className="text-muted small">Unassigned</span>}
                    </td>

                    {/* Actions */}
                    <td className="text-end" style={{ width: 70 }}>
                      <Dropdown align="end">
                        <Dropdown.Toggle variant="outline-secondary" size="sm">
                          <i className="bi bi-three-dots-vertical" />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                          <Dropdown.Item onClick={() => openTicket(t)}>
                            <i className="bi bi-box-arrow-up-right me-2" /> Open
                          </Dropdown.Item>
                          {!t.assignee && (
                            <Dropdown.Item onClick={() => { setCurrent(t); assignSelf(); }}>
                              <i className="bi bi-person-check me-2" /> Assign to me
                            </Dropdown.Item>
                          )}
                          <Dropdown.Divider />
                          {t.status !== "resolved" && t.status !== "closed" ? (
                            <Dropdown.Item onClick={() => { setCurrent(t); setStatusAction("resolved"); }}>
                              <i className="bi bi-check2-circle me-2" /> Resolve
                            </Dropdown.Item>
                          ) : (
                            <Dropdown.Item onClick={() => { setCurrent(t); setStatusAction("open"); }}>
                              <i className="bi bi-arrow-counterclockwise me-2" /> Reopen
                            </Dropdown.Item>
                          )}
                        </Dropdown.Menu>
                      </Dropdown>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      </Card>

      <TicketModal
        show={show}
        onHide={() => setShow(false)}
        ticket={current}
        comments={comments}
        posting={posting}
        onAssignSelf={() => assignSelf()}
        onUnassign={() => unassign()}
        onSetStatus={(s) => setStatusAction(s)}
        onSetPriority={(p) => setPriorityAction(p)}
        onAddPublicComment={(text, files, after) => addPublicComment(text, files, after)}
        onAddInternalNote={(text, files, after) => addInternalNote(text, files, after)}
      />

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toast.bg}
          show={toast.show}
          delay={toast.delay ?? 2200}
          autohide
          onClose={() => setToast(t => ({ ...t, show: false }))}
        >
          {toast.title ? (
            <Toast.Header closeButton={true}>
              <strong className="me-auto">{toast.title}</strong>
              <small className="text-muted">just now</small>
            </Toast.Header>
          ) : null}
          <Toast.Body className={toast.bg === "warning" ? "text-dark" : "text-white"}>
            {toast.body}
          </Toast.Body>
        </Toast>
      </ToastContainer>
        <style>{`
   .support-toolbar .nav-pills .nav-link{border-radius:999px;padding:.35rem .75rem}
.support-toolbar .nav-link .count{margin-left:.35rem;font-size:.75rem;opacity:.85}

.table-modern thead th.sticky-th{position:sticky;top:0;z-index:1;background:var(--bs-body-bg);backdrop-filter:saturate(180%) blur(8px);box-shadow:inset 0 -1px 0 var(--bs-border-color)}
.table-modern tbody tr{transition:background-color .18s ease}
.table-modern tbody tr:hover{background-color:var(--bs-secondary-bg)}
.table-modern .chip{display:inline-flex;align-items:center;border-radius:999px;padding:.25rem .5rem;font-weight:600;gap:.35rem}

.priority-pill{display:inline-flex;align-items:center;gap:.4rem;padding:.25rem .5rem;border-radius:999px;background:var(--bs-secondary-bg);font-weight:600;text-transform:capitalize}
.priority-pill .dot{width:.5rem;height:.5rem;border-radius:50%}
.priority-low .dot{background:var(--bs-success)}
.priority-normal .dot{background:var(--bs-secondary-color)}
.priority-high .dot{background:var(--bs-danger)}

.avatar-xs{width:28px;height:28px;border-radius:50%;object-fit:cover;box-shadow:0 0 0 2px var(--bs-body-bg)}
.avatar-fallback{display:inline-flex;align-items:center;justify-content:center;width:28px;height:28px;border-radius:50%;font-size:.7rem;font-weight:700;color:var(--bs-secondary-color);background:var(--bs-secondary-bg);box-shadow:0 0 0 2px var(--bs-body-bg)}

.comment-timeline>div{position:relative;padding-left:1rem}
.comment-timeline>div::before{content:"";position:absolute;left:.25rem;top:.35rem;width:6px;height:6px;border-radius:50%;background:var(--bs-primary)}
.comment-timeline>div+div{border-left:1px dashed var(--bs-border-color);margin-left:.5rem}


      `}</style>
    </Container>
  );
}
